import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import ButtonType from '@app/shared/button/button-type';
import { IOwnershipDropdown } from '@shared/common/common-types';
import { ClientOwnership } from '@shared/service-proxies/service-proxies';
import { isNil } from 'lodash';
import { debounceTime, Observable, of } from 'rxjs';

import { CONTACT_FILTERABLE_KEYS, IContact, IContactFilter } from './ContactFilter';
import { mockSourceDetailMapping, mockSources } from './mock-data';


interface ISource {
    label: string;
    value: string;
    items: { label: string; value: string }[];
}

@Component({
    selector: 'app-contacts-filter',
    templateUrl: './contacts-filter.component.html',
    styleUrls: ['./contacts-filter.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
class ContactsFilterComponent implements OnInit {

    @Input() ownerships: IOwnershipDropdown[] = [];

    @Output() filterChanged = new EventEmitter<IContactFilter>();

    form: FormGroup;

    filterObject: IContactFilter = {} as IContactFilter;

    filtersOpenState = {
        contactType: false,
        price: false,
        area: false,
        ownership: false,
        source: false,
        hasSource: false,
        advancedFilters: false,
        type: null,
    };

    iconClass: string = 'pi pi-chevron-down';

    selectedOwnership: IOwnershipDropdown | undefined;

    contacts: IContact[] | undefined;

    selectedContacts: IContact | undefined;

    sources = mockSources;

    sourceDetail: { name: string; value: string }[] = [];

    selectedSource: ISource | undefined;

    hasSource: boolean = false;

    loadedResultsCount: number = 0;

    hasSelection = false;

    tooltip: string;

    protected readonly ButtonType = ButtonType;

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
    ) {
    }

    get ownershipForm() {
        return this.form.get('ownership');
    }

    updateLoadedResultsCount(trueValues: Record<string, unknown>) {
        const { ...rest } = trueValues;
        this.loadedResultsCount = Object.keys(rest).length;
    }

    handleAllValuesCleared() {
        this.loadedResultsCount = 0;
    }

    ngOnInit() {

        this.contacts = [
            { name: 'Физическо лице', isIndividual: true },
            { name: 'Юридическо лице', isIndividual: false },
        ];


        this.buildForm()
            .subscribe((form) => {
                this.form = form;

                const paramMap = this.activatedRoute.snapshot.queryParamMap;

                CONTACT_FILTERABLE_KEYS.forEach((key) => {
                    const valueFromUrl = paramMap.get(key);
                    if (!isNil(valueFromUrl)) {
                        if (key === 'type') {
                            this.form.patchValue({ type: this.contacts.find((contact) => contact.name === valueFromUrl) });
                        } else if (key === 'ownership') {
                            this.form.patchValue({
                                ownership: this.ownerships.find((ownership) =>
                                    ownership.value === ClientOwnership[valueFromUrl]),
                            });
                        } else {
                            this.form.patchValue({ [key]: valueFromUrl });
                        }
                    } else {
                        if (key === 'ownership') {
                            this.form.patchValue({ ownership: this.ownerships[0] });
                        }
                    }
                });

                this.subscribeToFormChanges();
            });
    }

    buildForm(): Observable<FormGroup> {
        return of(this.fb.group({
            firstName: null,
            lastName: null,
            companyName: null,
            companyEic: null,
            isIndividual: true,
            source: [null],
            sourceDetail: [null],
            hasSource: [false],
            phone: null,
            email: null,
            createdOn: null,
            type: [null],
            ownership: [null],
        }));
    }

    subscribeToFormChanges() {
        this.ownershipForm.valueChanges.pipe(debounceTime(0))
            .subscribe(() => {
                this.onSubmit();
            });
    }

    onContactTypeChange(event: any) {
        const selectedContact = event.value;
        const { isIndividual } = selectedContact;


        this.form.patchValue({ isIndividual: isIndividual });

        if (isIndividual) {
            this.form.patchValue({ companyName: null, companyEic: null });
        } else {
            this.form.patchValue({ firstName: null, lastName: null });
        }
    }

    onSourceChange(event: any): void {
        const selectedSource = event.value;

        if (selectedSource) {
            this.sourceDetail = mockSourceDetailMapping[selectedSource] || [];
            this.form.patchValue({ hasSource: true });
        } else {
            this.sourceDetail = [];
            this.form.patchValue({ hasSource: false });
        }

        this.form.get('sourceDetail')
            ?.reset();
    }

    updateDropdownIcon(elementOpen) {
        return elementOpen
            ? 'pi pi-chevron-down rotate-icon rotate-up'
            : 'pi pi-chevron-down rotate-icon';
    }

    updateAdvancedFiltersDropdownIcon(elementOpen) {
        return elementOpen
            ? 'pi pi-chevron-right rotate-icon rotate-down'
            : 'pi pi-chevron-right rotate-icon';
    }

    tooltipTitle(labels: string[], defaultLabel: string): { label: string; tooltip: string } {
        let label = defaultLabel;
        let tooltip = '';

        if (labels.length > 3) {
            label = labels.slice(0, 3)
                .join(', ') + '...';
            tooltip = labels.join(', ');
        } else if (labels.length > 0) {
            label = labels.join(', ');
        }
        return { label, tooltip };
    }

    clearAll() {
        this.form.reset({
            firstName: null,
            lastName: null,
            companyName: null,
            companyEic: null,
            isIndividual: true,
            source: null,
            sourceDetail: null,
            hasSource: false,
            phone: null,
            email: null,
            createdOn: null,
        });

        this.sourceDetail = [];
        this.selectedOwnership = this.ownerships
            ? this.ownerships[0]
            : null;
        this.loadedResultsCount = 0;
        this.hasSelection = false;
    }

    onFilterContactTypeShow() {
        this.filtersOpenState.contactType = true;
    }

    onFilterContactTypeHide() {
        this.filtersOpenState.contactType = false;
    }

    onFilterOwnershipShow() {
        this.filtersOpenState.ownership = true;
    }

    onFilterOwnershipHide() {
        this.filtersOpenState.ownership = false;
    }

    onFilterSourceShow() {
        this.filtersOpenState.source = true;
    }

    onFilterSourceHide() {
        this.filtersOpenState.source = false;
    }

    onFilterSourceDetailShow() {
        this.filtersOpenState.hasSource = true;
    }

    onFilterSourceDetailHide() {
        this.filtersOpenState.hasSource = false;
    }

    toggleAdvancedFilters() {
        this.filtersOpenState.advancedFilters = !this.filtersOpenState.advancedFilters;
    }

    onSubmit() {
        const rawFilterValues: IContactFilter = this.form.value as IContactFilter;

        // Replace fields with value '' with null
        const cleanedFilterValues: IContactFilter = Object.entries(rawFilterValues)
            .reduce((acc, [key, value]) => {
                acc[key] = value === ''
                    ? null
                    : value;
                return acc;
            }, {} as IContactFilter);

        cleanedFilterValues.type = this.form.get('type')?.value?.name;
        this.filterChanged.emit(cleanedFilterValues);
    }
}

export default ContactsFilterComponent;
